import { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import TableCell from 'components/Shared/TableNew/TableCell'
import Dropdown from 'components/Shared/Dropdown/Index'
import LegendBar from 'components/Shared/LegendBar/Index'

import { useFetchApiRequestsQuery } from 'api/dashboard'

import { userTimeZoneFormat } from 'utils/time'

import { REQUEST_METHOD_COLOR_MAP, RESPONSE_STATUS_COLORS_MAP } from 'constants/colorMaps'
import colors from 'components/utils/colors'
import { requestsFilterValuesCodes, requestsFilterValuesEntities } from 'constants/constants'
import Tooltip from 'components/Shared/Tooltip/Index'
import IconLink from 'components/Shared/IconLink/Index'

const ALL = 'all'

const StyledWrapper = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 400px;
  min-height: 348px;
  flex-direction: column;
  padding: 10px 16px 10px 16px;
  background-color: #FFFFFF;
  box-sizing: border-box;
  border-radius: 3px;

`

const FiltersPanel = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colors.secondary90};
  padding-top: 16px;
`

const DropdownTitle = styled.div`
  font-size: 14px;
  padding-right: 15px;
`

const StyledDropdown = styled(Dropdown)`
`

const DropdownsSpacer = styled.div`
  width: 40px;
`

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: Exo;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.secondary20};
  margin-bottom: 15px;
`

const RequestMethodValue = styled.div`
  color: ${props => props.color};
  font-weight: 600;
`

const StyledLegendBar = styled(LegendBar)`
  align-self: flex-start;
  margin-top: 15px;
`

const ApiRequestsTable = ({ ticketingSystemDisplayId, userTimeZone }) => {
  const [code, setCode] = useState(ALL)
  const [entity, setEntity] = useState(ALL)
  const { t } = useTranslation()

  const { data, isFetching } =
    useFetchApiRequestsQuery({
      ticketingSystemDisplayId: ticketingSystemDisplayId,
      code: code,
      entity: entity,
    })

  const apiRequestsList = data?.requests || []

  const onSelectCode = (value) => {
    setCode(value)
  }

  const onSelectEntity = (value) => {
    setEntity(value)
  }

  const tooltipText = t('dashboardPage.latestRequestTooltipText')
  const tooltipId = 'api-table-tooltip'
  const notEmpty = apiRequestsList.length > 0
  const isActiveFilter = code !== ALL || entity !== ALL
  const legendItems = [
    { color: colors.success, label: t('common.success') },
    { color: colors.alert, label: t('common.failed') },
  ]

  return (
    <StyledWrapper>
      <LoadingIndicator loading={isFetching} small />
      <Title>
        {t('dashboardPage.recentApiRequestHeader')}
        <>
          <IconLink
            data-tooltip-content={tooltipText}
            data-tooltip-id={tooltipId}
            iconName='icon-help-16'
          />
          <Tooltip id='api-table-tooltip' />
        </>
      </Title>
      {(notEmpty || code !== ALL || entity !== ALL) && (
        <FiltersPanel>
          <DropdownTitle>{t('dashboardPage.code')}:</DropdownTitle>
          <StyledDropdown
            small
            name={code}
            itemsList={requestsFilterValuesCodes}
            onChange={onSelectCode}
            selectedItemId={code}
          />
          <DropdownsSpacer />
          <DropdownTitle>{t('dashboardPage.entity')}:</DropdownTitle>
          <StyledDropdown
            small
            name={entity}
            itemsList={requestsFilterValuesEntities}
            onChange={onSelectEntity}
            selectedItemId={entity}
          />
        </FiltersPanel>
      )}

      <Table width='100%'>
        <TableHead>
          <TableRow>
            <TableCell>{t('dashboardPage.request')}</TableCell>
            <TableCell>{t('dashboardPage.code')}</TableCell>
            {/* <TableCell>{t('dashboardPage.msgResponse')}</TableCell> */}
            <TableCell>{t('dashboardPage.url')}</TableCell>
            <TableCell>{t('dashboardPage.executed')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody clickable={false} hoverable={false}>
          {apiRequestsList.map(request => {
            const id = request.id
            const requestMethod = request.requestMethod
            const responseStatus = request.responseStatus
            const rowColor = responseStatus ? RESPONSE_STATUS_COLORS_MAP[responseStatus] : null
            const methodNameColor = REQUEST_METHOD_COLOR_MAP[requestMethod]
            const timeStr = userTimeZoneFormat(userTimeZone, request.createdAt)

            return (
              <TableRow key={id} color={rowColor}>
                <TableCell>
                  <RequestMethodValue color={methodNameColor}>
                    {requestMethod}
                  </RequestMethodValue>
                </TableCell>
                <TableCell>{request.responseCode}</TableCell>
                {/* <TableCell align='center'>NA</TableCell> */}
                <TableCell>{request.url}</TableCell>
                <TableCell>{timeStr}</TableCell>
              </TableRow>
            )
          })}
          <TableRowNoData
            data={apiRequestsList}
            hasFilterOrSearch={isActiveFilter}
          />
        </TableBody>
      </Table>
      {notEmpty && <StyledLegendBar items={legendItems} />}

    </StyledWrapper>
  )
}

ApiRequestsTable.propTypes = {
  ticketingSystemDisplayId: PropTypes.string,
  userTimeZone: PropTypes.string,
}

export default ApiRequestsTable
